import React from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Check, 
  Zap, 
  Shield, 
  TrendingUp,
  Users,
  FileText,
  Smartphone
} from 'lucide-react';

const Hero = () => {
  const easePremium = [0.22, 1, 0.36, 1];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.1, ease: easePremium },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: easePremium } },
  };

  const benefits = [
    { icon: Zap, text: 'Implementación rápida' },
    { icon: Shield, text: 'Datos 100% seguros' },
    { icon: TrendingUp, text: 'Escalable con tu negocio' }
  ];

  return (
    <section className="relative min-h-screen flex items-center bg-gradient-to-b from-white via-emerald-50/30 to-white overflow-hidden">
      
      {/* Fondo sutil con patrón de puntos (sugiere tecnología sin ser frío) */}
      <div 
        className="absolute inset-0 z-0 opacity-[0.015]"
        style={{
          backgroundImage: 'radial-gradient(#059669 1px, transparent 1px)',
          backgroundSize: '24px 24px'
        }}
      />

      <div className="container mx-auto px-6 lg:px-12 relative z-10 py-20 lg:py-28">
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-6xl mx-auto"
        >
          {/* Layout de dos columnas: Texto + Visual */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            
            {/* COLUMNA IZQUIERDA: Propuesta de valor clara */}
            <div>
              {/* Badge amigable */}
              <motion.div variants={itemVariants} className="mb-6">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-100 text-sm font-medium text-emerald-700">
                  <Zap className="w-4 h-4" />
                  Software que se adapta a tu negocio
                </span>
              </motion.div>

              {/* Título directo y orientado a beneficios */}
              <motion.h1 
                variants={itemVariants}
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.15] tracking-tight mb-6"
              >
                Digitaliza tu empresa con{' '}
                <span className="text-emerald-600">software inteligente</span>
              </motion.h1>

              {/* Subtítulo claro y cercano */}
              <motion.p 
                variants={itemVariants}
                className="text-lg text-slate-600 mb-8 leading-relaxed"
              >
                Sistemas de facturación, gestión contable y aplicaciones móviles 
                diseñados para PYMES, comercios y profesionales. Sin complicaciones, 
                con soporte real.
              </motion.p>

              {/* Beneficios clave (checks) */}
              <motion.div 
                variants={itemVariants}
                className="flex flex-wrap gap-4 mb-8"
              >
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <div key={index} className="flex items-center gap-2 text-sm font-medium text-slate-700">
                      <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                        <Check className="w-3 h-3 text-emerald-600" />
                      </div>
                      {benefit.text}
                    </div>
                  );
                })}
              </motion.div>

              {/* CTAs amigables */}
              <motion.div 
                variants={itemVariants}
                className="flex flex-col sm:flex-row gap-4 mb-8"
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="group flex items-center justify-center gap-2 bg-emerald-600 text-white px-7 py-3.5 rounded-lg font-semibold text-base transition-all hover:bg-emerald-700 hover:shadow-lg hover:shadow-emerald-600/20"
                >
                  Empezar ahora
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center justify-center gap-2 bg-white text-slate-700 px-7 py-3.5 rounded-lg font-semibold text-base border border-slate-200 transition-all hover:border-emerald-500 hover:text-emerald-700"
                >
                  Ver demostración
                </motion.button>
              </motion.div>

              {/* Social proof amigable */}
              <motion.div 
                variants={itemVariants}
                className="flex items-center gap-4 text-sm text-slate-500"
              >
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div 
                      key={i}
                      className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 border-2 border-white flex items-center justify-center text-white text-xs font-bold"
                    >
                      {String.fromCharCode(64 + i)}
                    </div>
                  ))}
                </div>
                <div>
                  <span className="font-semibold text-slate-700">+150 empresas</span> ya confían en nosotros
                </div>
              </motion.div>
            </div>

            {/* COLUMNA DERECHA: Visual de producto (mockup de software) */}
            <motion.div 
              variants={itemVariants}
              className="relative"
            >
              {/* Mockup de dashboard/interface */}
              <div className="relative bg-white rounded-2xl shadow-2xl shadow-slate-200/50 border border-slate-200 overflow-hidden">
                {/* Barra superior del "software" */}
                <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <div className="flex-1 text-center text-xs text-slate-400 font-medium">
                    Visioner7 - Panel de Control
                  </div>
                </div>

                {/* Contenido del mockup */}
                <div className="p-6 space-y-4">
                  {/* Header del dashboard */}
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <div className="text-sm text-slate-500">Bienvenido</div>
                      <div className="text-xl font-bold text-slate-900">Resumen del mes</div>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                    </div>
                  </div>

                  {/* Tarjetas de métricas */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-lg p-4 border border-emerald-100">
                      <div className="text-xs text-emerald-700 font-medium mb-1">Facturas emitidas</div>
                      <div className="text-2xl font-bold text-slate-900">247</div>
                      <div className="text-xs text-emerald-600 mt-1">+12% este mes</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-slate-200">
                      <div className="text-xs text-slate-500 font-medium mb-1">Clientes activos</div>
                      <div className="text-2xl font-bold text-slate-900">89</div>
                      <div className="text-xs text-slate-400 mt-1">+5 nuevos</div>
                    </div>
                  </div>

                  {/* Lista de documentos */}
                  <div className="space-y-2">
                    {[
                      { tipo: 'Factura', numero: 'F001-000123', estado: 'Aceptada', color: 'emerald' },
                      { tipo: 'Boleta', numero: 'B001-000456', estado: 'En proceso', color: 'yellow' },
                      { tipo: 'Nota Crédito', numero: 'NC001-000012', estado: 'Aceptada', color: 'emerald' }
                    ].map((doc, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className="w-4 h-4 text-slate-400" />
                          <div>
                            <div className="text-sm font-medium text-slate-900">{doc.numero}</div>
                            <div className="text-xs text-slate-500">{doc.tipo}</div>
                          </div>
                        </div>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full bg-${doc.color}-100 text-${doc.color}-700`}>
                          {doc.estado}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Elementos decorativos flotantes (sugieren "software en la nube") */}
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg border border-slate-200 p-3 flex items-center gap-2"
              >
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-900">Datos seguros</div>
                  <div className="text-[10px] text-slate-500">Encriptación AES-256</div>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg border border-slate-200 p-3 flex items-center gap-2"
              >
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Users className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-900">Soporte 24/7</div>
                  <div className="text-[10px] text-slate-500">Respuesta en &lt;1h</div>
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Sección inferior: Logos de clientes (opcional, si tienes) 
          <motion.div 
            variants={itemVariants}
            className="mt-20 pt-12 border-t border-slate-100"
          >
            <p className="text-center text-sm font-medium text-slate-400 uppercase tracking-wider mb-8">
              Empresas que confían en Visioner7
            </p>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12 opacity-60">
              {['Comercial Andina', 'Distribuidora Norte', 'Servicios del Sur', 'Tech Solutions', 'Grupo Empresarial'].map((brand, i) => (
                <div key={i} className="text-slate-700 font-bold text-lg tracking-tight">
                  {brand}
                </div>
              ))}
            </div>
          </motion.div>*/}

        </motion.div>
      </div>
    </section>
  );
};

export default Hero;